import _, { isNil } from 'lodash';
import client from './RestClient';
import { Advertiser, Merchant, ShareSegment } from 'core/advertiser/Advertiser';
import { Pageable } from './Pageable';
import { Pagination } from 'core/pagination/Pagination';
import { CompanyMember } from 'core/companyMember/CompanyMember';
import { Account } from 'core';
import { CompanyRole } from 'core/companyMember/CompanyRole';
import { AxiosInstance } from 'axios';
import {
  BeaconConnection,
  BeaconConnectionFormData,
  LineBotInfo
} from 'core/advertiser/BeaconConnection';

export interface AdvertiserWebService {
  getAdvertiser(advertiserId): Promise<Advertiser>;
  getAdvertiserCategory(advertiserId): Promise<string>;
  createAdvertiser(advertiser: Partial<Advertiser>): Promise<number>;
  updateAdvertiser(advertiser: Partial<Advertiser>): Promise<number>;
  getAdvertisers(
    agencyId: number | string | undefined,
    pageable: Pageable,
    search?: string
  ): Promise<AdvertisersWithPagination>;
  getAdvertiserOptions(agencyId?: number | null): Promise<Array<SelectOptions>>;
  getAdvertiserMembers(
    advertiserId: number | string
  ): Promise<Array<CompanyMember>>;
  deleteAdvertiserMembers(
    advertiserId: number,
    accountId: number
  ): Promise<void>;
  getAdvertiserMember(
    advertiserId: number,
    accountId: number
  ): Promise<CompanyMember>;
  createAdvertiserMember(
    advertiserId: number,
    companyRole: CompanyRole
  ): Promise<void>;
  updateAdvertiserMember(
    advertiserId: number,
    companyRole: CompanyRole
  ): Promise<void>;
  updateAdvertiserShareSegment(
    advertiserId: number,
    segmentSourceIds: number[]
  ): Promise<void>;
  createBeaconConnection(
    advertiserId: number,
    beaconConnection: BeaconConnectionFormData
  ): Promise<void>;
  updateBeaconConnection(
    advertiserId: number,
    id: number,
    beaconConnection: BeaconConnectionFormData
  ): Promise<void>;
  getBeaconConnections(advertiserId: number): Promise<BeaconConnection[]>;
  getBeaconConnectionOptions(advertiserId: number): Promise<SelectOptions[]>;
  getLineBeaconCoupons(
    advertiserId: number,
    beaconConnectionId: string
  ): Promise<SelectOptions[]>;
  getLineBotInfo(
    advertiserId: number,
    channelAccessToken: string
  ): Promise<LineBotInfo>;
  getMerchants(
    agencyId: number | string | undefined,
    pageable: Pageable,
    search?: string
  ): Promise<MerchantsWithPagination>;
}

export type AdvertisersWithPagination = {
  pagination: Pagination;
  advertisers: Array<Advertiser>;
};

export type MerchantsWithPagination = {
  pagination: Pagination;
  merchants: Merchant[];
};

function wrapPagination(json: any): any {
  return {
    page: _.get(json, 'page', 1),
    size: _.get(json, 'size', 10),
    totalCount: _.get(json, 'totalCount', 0)
  };
}

function wrapShareSegment(json: any): ShareSegment {
  return {
    agencyId: _.get(json, 'agencyId'),
    advertiserId: _.get(json, 'advertiserId'),
    agencyName: _.get(json, 'agencyName'),
    advertiserName: _.get(json, 'advertiserName')
  };
}

function wrapAdvertiser(json: any): Advertiser {
  const note = _.get(json, 'note', '');
  return {
    ...json,
    id: _.get(json, 'advertiserId'),
    agencyId: _.get(json, 'agencyId'),
    advertiserName: _.get(json, 'advertiserName', ''),
    website: _.get(json, 'website', ''),
    category: _.get(json, 'category', ''),
    note: note === null ? undefined : note,
    productFeedID: _.get(json, 'productFeedID', ''),
    isTrackingCodeReady: _.get(json, 'isTrackingCodeReady', false),
    isProductFeedReady: _.get(json, 'isProductFeedReady', false),
    limitPublisher: _.get(json, 'limitPublisher', []),
    shareSegment: _.get(json, 'shareSegment', []).map((shareSegment) =>
      wrapShareSegment(shareSegment)
    )
  };
}

function wrapCompanyMember(json: any): CompanyMember {
  return {
    id: _.get(json, 'id'),
    account: wrapAccount(_.get(json, 'account')),
    roleName: _.get(json, 'roleName')
  };
}

function wrapAccount(json: any): Account {
  return {
    language: _.get(json, 'language'),
    activated: _.get(json, 'activated'),
    email: _.get(json, 'email'),
    name: _.get(json, 'name'),
    id: _.get(json, 'account_id'),
    isAdmin: _.get(json, 'is_admin'),
    status: _.get(json, 'status'),
    actors: []
  };
}

export class RestfulAdvertiserWebService implements AdvertiserWebService {
  restClient: AxiosInstance;

  constructor(restClient: AxiosInstance = client) {
    this.restClient = restClient;
  }

  async getAdvertiser(advertiserId): Promise<Advertiser> {
    const response = await this.restClient.get(
      `/v2/advertisers/${advertiserId}`
    );
    return wrapAdvertiser(response.data);
  }

  async getAdvertiserCategory(advertiserId): Promise<string> {
    const response = await this.restClient.get(
      `/v2/advertisers/${advertiserId}/category`
    );
    return response.data;
  }

  async createAdvertiser(advertiser: Partial<Advertiser>): Promise<number> {
    const response = await this.restClient.post(`/v2/advertisers`, advertiser);
    return _.get(response.data, 'advertiserId', 0);
  }

  async updateAdvertiser(advertiser: Partial<Advertiser>): Promise<number> {
    const response = await this.restClient.put(`/v2/advertisers`, {
      ..._.omit(advertiser, ['id']),
      advertiserId: advertiser.id
    });
    return _.get(response.data, 'advertiserId', 0);
  }

  async getAdvertisers(
    agencyId: number | string | undefined,
    pageable: Pageable,
    search = ''
  ): Promise<AdvertisersWithPagination> {
    let { page, sizePerPage, sort, direction } = pageable;
    sort = sort ? sort : 'id';
    direction = direction ? direction : 'desc';
    let url = `/v2/advertisers?search=${encodeURIComponent(search)}&page=${page}&size=${sizePerPage}&sort=${sort},${direction}`;
    url = agencyId ? `${url}&agencyId=${agencyId}` : url;
    const response = await this.restClient.get(url);
    return {
      advertisers: _.defaultTo(response.data.records, []).flatMap((json: any) =>
        wrapAdvertiser(json)
      ),
      pagination: wrapPagination(response.data.pagination)
    };
  }

  async getAdvertiserOptions(
    agencyId?: number | null
  ): Promise<Array<SelectOptions>> {
    const path = !isNil(agencyId)
      ? `/v2/advertisers/options?agencyId=${agencyId}`
      : '/v2/advertisers/options';
    const response = await this.restClient.get(path);
    return _.get(response, 'data.records', []);
  }

  async getAdvertiserMembers(
    advertiserId: number | string
  ): Promise<Array<CompanyMember>> {
    const response = await this.restClient.get(
      `/v2/advertisers/${advertiserId}/members`
    );
    return _.get(response, 'data.records', []).map((member) =>
      wrapCompanyMember(member)
    );
  }

  async deleteAdvertiserMembers(
    advertiserId: number,
    accountId: number
  ): Promise<void> {
    return this.restClient.delete(
      `/v2/advertisers/${advertiserId}/members/${accountId}`
    );
  }

  async getAdvertiserMember(
    advertiserId: number,
    accountId: number
  ): Promise<CompanyMember> {
    const response = await this.restClient.get(
      `/v2/advertisers/${advertiserId}/members/${accountId}`
    );
    return wrapCompanyMember(_.get(response, 'data', []));
  }

  async createAdvertiserMember(
    advertiserId: number,
    companyRole: CompanyRole
  ): Promise<void> {
    return this.restClient.post(
      `/v2/advertisers/${advertiserId}/members?email=${companyRole.email}&role_name=${companyRole.role}`
    );
  }

  async updateAdvertiserMember(
    advertiserId: number,
    companyRole: CompanyRole
  ): Promise<void> {
    return this.restClient.put(
      `/v2/advertisers/${advertiserId}/members?email=${companyRole.email}&role_name=${companyRole.role}`
    );
  }

  async updateAdvertiserShareSegment(
    advertiserId: number,
    segmentSourceIds: number[]
  ): Promise<void> {
    return this.restClient.put(
      `/v2/advertisers/${advertiserId}/share-segment`,
      {
        advertiserIds: segmentSourceIds
      }
    );
  }

  async createBeaconConnection(
    advertiserId: number,
    beaconConnection: BeaconConnectionFormData
  ): Promise<void> {
    return this.restClient.post(
      `/v2/advertisers/${advertiserId}/beacon-connections`,
      beaconConnection
    );
  }

  async updateBeaconConnection(
    advertiserId: number,
    id: number,
    beaconConnection: BeaconConnectionFormData
  ): Promise<void> {
    return this.restClient.put(
      `/v2/advertisers/${advertiserId}/beacon-connections/${id}`,
      beaconConnection
    );
  }

  async getBeaconConnections(
    advertiserId: number
  ): Promise<BeaconConnection[]> {
    const response = await this.restClient.get(
      `/v2/advertisers/${advertiserId}/beacon-connections`
    );
    return response.data.records;
  }

  async getBeaconConnectionOptions(
    advertiserId: number
  ): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      `/v2/advertisers/${advertiserId}/beacon-connection-options`
    );
    return response.data.records.map((record) => ({
      value: record.value,
      label: record.label,
      extra: record.beaconType
    }));
  }

  async getLineBeaconCoupons(
    advertiserId: number,
    beaconConnectionId: string
  ): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      `/v2/advertisers/${advertiserId}/beacon-connections/${beaconConnectionId}/line/coupons`
    );
    return _.defaultTo(response.data.items, []).map((item) => ({
      label: item.title,
      value: item.couponId
    }));
  }

  async getLineBotInfo(
    advertiserId: number,
    channelAccessToken: string
  ): Promise<LineBotInfo> {
    const response = await this.restClient.post(
      `/v2/advertisers/${advertiserId}/beacon-connections/line/bot-info`,
      { channelAccessToken }
    );
    return response.data;
  }

  async getMerchants(
    agencyId: number | string | undefined,
    pageable: Pageable,
    search = ''
  ): Promise<MerchantsWithPagination> {
    let { page, sizePerPage, sort, direction } = pageable;
    sort = sort ? sort : 'id';
    direction = direction ? direction : 'desc';
    let url = `/v2/ads-beacon/merchants?search=${encodeURIComponent(search)}&page=${page}&size=${sizePerPage}&sort=${sort},${direction}`;
    url = agencyId ? `${url}&agencyId=${agencyId}` : url;
    const response = await this.restClient.get(url);
    return {
      merchants: _.defaultTo(response.data.records, []),
      pagination: wrapPagination(response.data.pagination)
    };
  }
}
